import { TimeSlot } from "./types";

/**
 * Generate 15-minute time slots for the next 23 hours starting from `fromDate`
 * (defaults to now), rounded up to the next 15-minute boundary.
 *
 * Both "startTime" and "endTime" use the same logic — 23 h × 4 slots = 92 slots.
 * Pass `type` for future differentiation if needed; behaviour is identical today.
 */
export function generateTimeSlots(
  fromDate?: Date,
  type?: "startTime" | "endTime",
): string[] {
  const base = fromDate ? new Date(fromDate) : new Date();

  // Round up to the next 15-minute boundary
  const remainder = base.getMinutes() % 15;
  if (remainder !== 0) {
    base.setMinutes(base.getMinutes() + (15 - remainder), 0, 0);
  } else {
    base.setSeconds(0, 0);
  }

  const slots: string[] = [];
  const cursor = new Date(base);
  // startTime → 23 h window (92 slots), endTime → 24 h window (96 slots)
  const totalSlots = type === "endTime" ? 24 * 4 : 23 * 4;

  for (let i = 0; i < totalSlots; i++) {
    const h = cursor.getHours();
    const m = cursor.getMinutes();
    const period = h < 12 ? "am" : "pm";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    const displayMinute = m.toString().padStart(2, "0");
    slots.push(`${displayHour}:${displayMinute} ${period}`);
    cursor.setMinutes(cursor.getMinutes() + 15);
  }

  return slots;
}

export function isoToTimeSlot(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (isNaN(date.getTime())) return "";

  const h = date.getHours();
  const m = date.getMinutes();
  const period = h < 12 ? "am" : "pm";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const displayMinute = m.toString().padStart(2, "0");
  return `${displayHour}:${displayMinute} ${period}`;
}

export function timeSlotToIso(
  slot: string,
  referenceDate?: Date | string | null,
): string {
  if (!slot) return "";

  const match = slot
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (!match) return "";

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3];

  if (period === "am" && hours === 12) hours = 0;
  if (period === "pm" && hours !== 12) hours += 12;

  const base = referenceDate
    ? typeof referenceDate === "string"
      ? new Date(referenceDate)
      : referenceDate
    : new Date();

  const result = new Date(base);
  result.setHours(hours, minutes, 0, 0);
  return result.toISOString();
}

export const parseTime = (text: string): TimeSlot | null => {
  const clean = text.trim().toLowerCase();

  const match = clean.match(/^(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?$/);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2] || "0", 10);
  const period = match[3];

  if (minutes > 59 || hours > 24) return null;

  if (period) {
    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  const iso = date.toISOString();
  const label = isoToTimeSlot(iso);

  return { value: label, label, iso };
};
